import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { TsForm } from '../../components/form/Form';
import { SubmitCancelButtons } from '../../components/form/SubmitCancelButtons';
import { TextFieldWithOptionalLabel } from '../../components/form/TextInput';
import { handleMutationResult } from '../../helpers/toastHelper';
import { Device, useCreateDeviceMutation, useGetDevicesForLocationQuery } from '../../services/gql/graphql.generated';
import strings from './devices.translations';

interface AddDeviceFormProps {
  locationId: number;
  onComplete: () => void;
}

interface AddDeviceFields {
  name: string;
}

export const AddDeviceForm: FC<AddDeviceFormProps> = ({ locationId, onComplete }) => {
  const methods = useForm<AddDeviceFields>();

  const [{ fetching }, createDevice] = useCreateDeviceMutation();
  const navigate = useNavigate();

  const [{ data }] = useGetDevicesForLocationQuery({ variables: { id: Number(locationId) } });

  const onChange = (values: string) => {
    methods.clearErrors('name');
    const devices = data?.devicesForLocation as Device[];

    const getDeviceNameFromList = devices.find(device => device?.name === values)?.name;

    if (getDeviceNameFromList) {
      methods.setError('name', {
        type: 'custom',
        message: strings.addDeviceModal.uniqueName
      });
      return;
    }
  };
  const onSubmit = async (values: AddDeviceFields) => {
    const result = await createDevice({
      args: {
        name: values.name,
        locationId: locationId!
      }
    });

    await handleMutationResult(result, {
      successMessage: "Device successfully added.",
      errorMessage: "Unable to add device.",
      onSuccess: data => {
        if (data?.createDevice?.id) {
          navigate(data.createDevice.id);
        }
        onComplete();
      }
    });
  };

  return (
    <TsForm methods={methods} onSubmit={onSubmit}>
      <div className="flex flex-col space-y-5 xl:w-96 mt-3">
        <TextFieldWithOptionalLabel
          id="name"
          name="name"
          title={strings.addDeviceModal.nameField}
          validationMessage={strings.addDeviceModal.nameRequired}
          required
          placeholder={strings.addDeviceModal.namePlaceholder}
          customValidatorMessage={methods.formState.errors.name?.message}
          onChange={values => onChange(values)}
          value={methods.watch('name') || ''}
        />
        <div className="flex items-center justify-end gap-3">
          <SubmitCancelButtons
            disabled={methods.formState.errors.name?.message ? true : false}
            onComplete={onComplete}
            loading={fetching}
            submitText={strings.common.create}
          ></SubmitCancelButtons>
        </div>
      </div>
    </TsForm>
  );
};
